import React, { useEffect, useMemo, useState } from "react";
import { FiSearch, FiUserPlus, FiEdit2, FiTrash2, FiX, FiShield } from "react-icons/fi";
import { toast } from "react-toastify";
import { Trans, useTranslation } from "react-i18next";
import adminAxios from "../../utils/adminAxios";
import { getAdminAuth } from "../../utils/adminAuth";

const emptyForm = { admin_id: null, adminName: "", password: "" };

const Admins = () => {
  const { t } = useTranslation();
  const [admins, setAdmins] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState({ open: false, mode: "create" });
  const [form, setForm] = useState(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const currentAdminId = getAdminAuth()?.admin?.id;

  const filteredAdmins = useMemo(
    () =>
      admins.filter((a) =>
        a.adminName?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    [admins, searchTerm]
  );

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const response = await adminAxios.get("admins/get-all-admins");
      setAdmins(response.data.data || []);
    } catch (error) {
      toast.error(error.response?.data?.message || t("dashboard.admins.toast.fetchError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const openCreateModal = () => {
    setForm(emptyForm);
    setModal({ open: true, mode: "create" });
  };

  const openEditModal = (admin) => {
    setForm({ admin_id: admin.id, adminName: admin.adminName, password: "" });
    setModal({ open: true, mode: "edit" });
  };

  const closeModal = () => {
    setModal({ open: false, mode: "create" });
    setForm(emptyForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal.mode === "create") {
        await adminAxios.post("admins/create-admin", {
          adminName: form.adminName,
          password: form.password,
        });
        toast.success(t("dashboard.admins.toast.createSuccess"));
      } else {
        await adminAxios.patch("admins/update-admin", {
          admin_id: form.admin_id,
          adminName: form.adminName,
          ...(form.password ? { password: form.password } : {}),
        });
        toast.success(t("dashboard.admins.toast.updateSuccess"));
      }
      closeModal();
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.message || t("dashboard.admins.toast.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await adminAxios.delete("admins/delete-admin", {
        params: { admin_id: deleteTarget.id },
      });
      toast.success(t("dashboard.admins.toast.deleteSuccess"));
      setDeleteTarget(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.message || t("dashboard.admins.toast.deleteError"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t("dashboard.admins.title")}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {t("dashboard.admins.subtitle")}
          </p>
        </div>
        <button
          onClick={openCreateModal}
          className="mt-4 lg:mt-0 flex items-center justify-center space-x-2 rtl:space-x-reverse px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all"
        >
          <FiUserPlus className="w-4 h-4" />
          <span>{t("dashboard.admins.addButton")}</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <FiSearch className="absolute left-3 rtl:left-auto rtl:right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
        <input
          type="text"
          placeholder={t("dashboard.admins.search.placeholder")}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 rtl:pl-4 rtl:pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
        />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.admins.table.admin")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.admins.table.createdAt")}
                </th>
                <th className="px-6 py-4 text-left rtl:text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("dashboard.admins.table.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
              {filteredAdmins.map((admin) => (
                <tr
                  key={admin.id}
                  className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center mr-3 rtl:mr-0 rtl:ml-3">
                        <FiShield className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                          {admin.adminName}
                          {admin.id === currentAdminId && (
                            <span className="ml-2 rtl:ml-0 rtl:mr-2 text-xs text-blue-500">
                              {t("dashboard.admins.table.you")}
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {t("dashboard.admins.table.id")}: {admin.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                    {admin.createdAt
                      ? new Date(admin.createdAt).toLocaleDateString()
                      : t("dashboard.admins.table.notAvailable")}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-3 rtl:space-x-reverse">
                      <button
                        onClick={() => openEditModal(admin)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                      >
                        <FiEdit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteTarget(admin)}
                        disabled={admin.id === currentAdminId}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <FiTrash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredAdmins.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 dark:text-gray-500 text-6xl mb-4">🛡️</div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              {t("dashboard.admins.emptyState.title")}
            </h3>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {modal.open && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={closeModal}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {modal.mode === "create"
                    ? t("dashboard.admins.modal.createTitle")
                    : t("dashboard.admins.modal.editTitle")}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("dashboard.admins.modal.fields.adminName.label")}
                  </label>
                  <input
                    type="text"
                    required
                    value={form.adminName}
                    onChange={(e) =>
                      setForm({ ...form, adminName: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t("dashboard.admins.modal.fields.password.label")}{" "}
                    {modal.mode === "edit" && (
                      <span className="text-gray-400 text-xs">
                        {t("dashboard.admins.modal.fields.password.editHint")}
                      </span>
                    )}
                  </label>
                  <input
                    type="password"
                    required={modal.mode === "create"}
                    minLength={6}
                    value={form.password}
                    onChange={(e) =>
                      setForm({ ...form, password: e.target.value })
                    }
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  />
                </div>

                <div className="flex justify-end space-x-3 rtl:space-x-reverse pt-2">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    {t("dashboard.admins.modal.buttons.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg shadow-md hover:shadow-lg transition-all disabled:opacity-50"
                  >
                    {saving
                      ? t("dashboard.admins.modal.buttons.saving")
                      : t("dashboard.admins.modal.buttons.save")}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setDeleteTarget(null)}
          />
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-sm p-6">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">
                {t("dashboard.admins.deleteModal.title")}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                <Trans
                  i18nKey="dashboard.admins.deleteModal.confirmText"
                  values={{ name: deleteTarget.adminName }}
                  components={{ strong: <strong /> }}
                />
              </p>
              <div className="flex justify-end space-x-3 rtl:space-x-reverse">
                <button
                  onClick={() => setDeleteTarget(null)}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  {t("dashboard.admins.deleteModal.cancel")}
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  {t("dashboard.admins.deleteModal.confirmButton")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Admins;
